import React from 'react';
import { useFocusable, FocusContext } from '@noriginmedia/norigin-spatial-navigation';
import styled from 'styled-components';

// Composant de base focusable
export const Focusable = ({
  children,
  onPress,
  onFocus,
  className = '',
  focusedClassName = 'tv-focused',
  as: Component = 'div',
  focusKey,
  disabled = false,
  ...props
}) => {
  const { ref, focused } = useFocusable({
    onEnterPress: disabled ? undefined : onPress,
    onFocus,
    focusKey,
    focusable: !disabled
  });

  return (
    <Component
      ref={ref}
      className={`${className} ${focused ? focusedClassName : ''}`}
      {...props}
    >
      {children}
    </Component>
  );
};

// Container focusable qui peut contenir d'autres éléments focusables
export const FocusableSection = ({
  children,
  focusKey,
  className = '',
  onFocus,
  onBackPress,
  saveLastFocusedChild = true,
  isFocusBoundary = false,
  autoRestoreFocus = true,
  ...props
}) => {
  const {
    ref,
    focusKey: currentFocusKey,
    focusSelf,
    hasFocusedChild
  } = useFocusable({
    focusKey,
    trackChildren: true,
    saveLastFocusedChild,
    isFocusBoundary,
    autoRestoreFocus,
    onFocus,
    onArrowPress: (direction) => {
      // Si onBackPress est défini et qu'on appuie sur gauche au bord, déclencher le back
      if (onBackPress && direction === 'left') {
        // Laisser la navigation par défaut se faire, onBackPress sera géré via Escape/Backspace
      }
      return true; // Continuer la navigation
    }
  });

  return (
    <FocusContext.Provider value={currentFocusKey}>
      <div
        ref={ref}
        className={className}
        {...props}
      >
        {typeof children === 'function'
          ? children({ focusSelf, hasFocusedChild })
          : children}
      </div>
    </FocusContext.Provider>
  );
};

// Bouton focusable stylisé
const StyledFocusableButton = styled.button`
  transition: all 0.2s ease;

  &.tv-focused {
    outline: 3px solid #632ce4;
    outline-offset: 2px;
    box-shadow: 0 0 20px rgba(99, 44, 228, 0.5);
    transform: scale(1.02);
  }
`;

export const FocusableButton = ({
  children,
  onPress,
  className = '',
  disabled = false,
  show,
  focusKey,
  ...props
}) => {
  // Si show est explicitement false, l'élément n'est pas focusable
  const isFocusable = !disabled && show !== false;

  const { ref, focused } = useFocusable({
    onEnterPress: isFocusable ? onPress : undefined,
    focusable: isFocusable,
    focusKey
  });

  return (
    <StyledFocusableButton
      ref={ref}
      className={`${className} ${focused ? 'tv-focused' : ''}`}
      disabled={disabled}
      onClick={onPress}
      $show={show}
      {...props}
    >
      {children}
    </StyledFocusableButton>
  );
};

// Card focusable (pour les grilles de photos, contacts, etc.)
const StyledFocusableCard = styled.div`
  transition: all 0.2s ease;
  cursor: pointer;

  &.tv-focused {
    outline: 3px solid #632ce4;
    outline-offset: 2px;
    box-shadow: 0 0 25px rgba(99, 44, 228, 0.6);
    transform: scale(1.03);
    z-index: 10;
  }
`;

export const FocusableCard = ({
  children,
  onPress,
  className = '',
  ...props
}) => {
  const { ref, focused } = useFocusable({
    onEnterPress: onPress
  });

  return (
    <StyledFocusableCard
      ref={ref}
      className={`${className} ${focused ? 'tv-focused' : ''}`}
      onClick={onPress}
      {...props}
    >
      {children}
    </StyledFocusableCard>
  );
};

// Hook pour gérer le back/escape
export const useBackHandler = (onBack) => {
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignorer si on est dans un champ de saisie (input, textarea, contenteditable)
      const activeElement = document.activeElement;
      const isTyping = activeElement && (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.isContentEditable
      );

      // Pour Backspace, ne pas interférer si on tape dans un champ
      if (e.key === 'Backspace' && isTyping) {
        return; // Laisser le comportement par défaut (effacer le caractère)
      }

      if ((e.key === 'Backspace' || e.key === 'Escape') && onBack) {
        e.preventDefault();
        e.stopPropagation();
        onBack();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onBack]);
};

// Export de setFocus pour permettre de focus un élément spécifique
export { setFocus } from '@noriginmedia/norigin-spatial-navigation';
